import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowDownCircle, CalendarClock, CreditCard, Receipt, Wallet } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { useAuth } from '@/lib/auth-context';
import { formatarData, formatBrl } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { useRealtimeInvalidate } from '@/lib/use-realtime-invalidate';

interface Pagamento {
  seq_pagamento: number;
  cod_modalidade: string;
  cod_metodo: string;
  val_pagamento: number;
  cod_status_asaas: string;
  dta_vencimento: string;
  dta_recebimento: string | null;
}

function statusVariant(s: string): 'success' | 'danger' | 'warning' | 'neutral' {
  if (s === 'CONFIRMED' || s === 'RECEIVED') return 'success';
  if (s === 'OVERDUE') return 'danger';
  if (s === 'REFUNDED') return 'neutral';
  return 'warning';
}

export function PagamentosPage() {
  const { user } = useAuth();

  const pagamentos = useQuery({
    queryKey: ['cliente-pagamentos', user?.id],
    queryFn: async () => {
      const { data: cli } = await supabase
        .from('tab_clientes')
        .select('seq_cliente')
        .eq('cod_auth_user', user!.id)
        .single();
      if (!cli) return [];
      const { data, error } = await supabase
        .from('tab_pagamentos')
        .select(
          'seq_pagamento, cod_modalidade, cod_metodo, val_pagamento, cod_status_asaas, dta_vencimento, dta_recebimento',
        )
        .eq('seq_cliente', cli.seq_cliente)
        .order('dta_vencimento', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as Pagamento[];
    },
    enabled: !!user,
  });

  useRealtimeInvalidate('tab_pagamentos', ['cliente-pagamentos', user?.id]);

  if (pagamentos.isLoading) {
    return (
      <div className="space-y-3 p-4 pb-24">
        <Skeleton className="h-36 rounded-3xl" />
        <Skeleton className="h-20 rounded-2xl" />
        <Skeleton className="h-20 rounded-2xl" />
      </div>
    );
  }

  const lista = pagamentos.data ?? [];
  const ultimoRecebido = lista.find(
    (p) => p.cod_status_asaas === 'CONFIRMED' || p.cod_status_asaas === 'RECEIVED',
  );

  return (
    <div className="space-y-5 p-4 pb-24">
      <motion.div
        className="octa-page-header"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1>Pagamentos</h1>
        <p>Histórico de cobranças, recebidos e em aberto.</p>
      </motion.div>

      {/* Resumo do mais recente */}
      {ultimoRecebido && (
        <motion.div
          initial={{ opacity: 0, y: 14, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        >
          <Card variant="default" className="overflow-hidden">
            <CardContent className="space-y-3 p-5">
              <div className="flex items-center justify-between">
                <span className="text-overline text-muted-foreground">Último pagamento</span>
                <StatusBadge dot variant="success">
                  Pago
                </StatusBadge>
              </div>
              <p className="octa-numero-hero">{formatBrl(ultimoRecebido.val_pagamento)}</p>
              <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <CalendarClock className="h-3.5 w-3.5" />
                Recebido em{' '}
                {ultimoRecebido.dta_recebimento
                  ? formatarData(ultimoRecebido.dta_recebimento)
                  : formatarData(ultimoRecebido.dta_vencimento)}
                <span className="text-muted-foreground/40">·</span>
                <CreditCard className="h-3.5 w-3.5" />
                {ultimoRecebido.cod_metodo}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Timeline */}
      {lista.length === 0 ? (
        <EmptyState
          variant="hero"
          icon={Receipt}
          title="Sem pagamentos"
          description="Suas cobrancas vao aparecer aqui assim que você assinar um plano."
        />
      ) : (
        <div className="space-y-3">
          <h2 className="octa-section-title">Histórico</h2>
          <ul className="relative space-y-2">
            {/* Linha vertical da timeline */}
            <div aria-hidden className="bg-border absolute bottom-3 left-[18px] top-3 w-px" />
            {lista.map((p, i) => {
              const variant = statusVariant(p.cod_status_asaas);
              const cor =
                variant === 'success'
                  ? 'bg-emerald-500'
                  : variant === 'danger'
                    ? 'bg-rose-500'
                    : variant === 'warning'
                      ? 'bg-amber-500'
                      : 'bg-slate-400';
              return (
                <motion.li
                  key={p.seq_pagamento}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.04, duration: 0.35 }}
                  className="relative pl-10"
                >
                  {/* Dot da timeline */}
                  <div className="absolute left-3 top-5 z-10 flex h-3 w-3 items-center justify-center">
                    <div className={`h-3 w-3 rounded-full ${cor} ring-background ring-4`} />
                  </div>
                  <Card variant="default" className="octa-card-tap">
                    <CardContent className="flex items-center justify-between gap-3 p-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-base font-bold tabular-nums">
                          {formatBrl(p.val_pagamento)}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {p.cod_modalidade} · {p.cod_metodo}
                        </p>
                        <p className="text-muted-foreground mt-0.5 text-[11px]">
                          Venc.: {formatarData(p.dta_vencimento)}
                        </p>
                      </div>
                      <StatusBadge variant={variant}>{p.cod_status_asaas}</StatusBadge>
                    </CardContent>
                  </Card>
                </motion.li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Wallet hint */}
      {lista.length > 0 && (
        <Card variant="flat" className="border-dashed">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="bg-muted/70 flex h-9 w-9 items-center justify-center rounded-xl">
              <Wallet className="text-muted-foreground h-4 w-4" />
            </div>
            <p className="text-muted-foreground text-xs">
              Toque em um pagamento para ver detalhes (em breve por aqui).
            </p>
            <ArrowDownCircle className="text-muted-foreground/40 ml-auto h-4 w-4" />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
